import { useState } from "react";
import { ArrowUp, ArrowDown } from "lucide-react";
import { api } from '../utils/api';

export default function AddSupplierModal({ open, onClose, onAddSupplier }) {
  const [newSupplier, setNewSupplier] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    gstNumber: "",
    currentBalance: 0,
    isActive: true,
    shippingAddress: {
      flatBuildingNumber: "",
      areaLocality: "",
      pinCode: "",
      city: "",
      state: "",
    },
  });
  const [showPartyDetails, setShowPartyDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [gstNumberError, setGstNumberError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith("shippingAddress.")) {
      const field = name.split(".")[1];
      setNewSupplier((prev) => ({
        ...prev,
        shippingAddress: {
          ...prev.shippingAddress,
          [field]: value,
        },
      }));
    } else {
      setNewSupplier((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : (name === "currentBalance" ? Number.parseFloat(value) || 0 : value),
      }));
      if (name === "gstNumber") {
        const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
        if (value.trim() === "") {
          setGstNumberError("GST Number is required");
        } else if (!gstinRegex.test(value)) {
          setGstNumberError("Invalid GST Number format (e.g., 27AAAAA0000A1Z5)");
        } else if (value.length > 15) {
          setGstNumberError("GST Number must be 15 characters");
        } else {
          setGstNumberError("");
        }
      }
      if (name === "phone") {
        const phoneRegex = /^[6-9]\d{9}$/;
        if (value.trim() !== "" && !phoneRegex.test(value)) {
          setPhoneError("Please provide a valid Indian mobile number (e.g., 9876543210)");
        } else {
          setPhoneError("");
        }
      }
    }
  };

  const handleAddSupplier = async () => {
    // Validate required fields
    if (!newSupplier.name.trim()) {
      setError("Supplier Name is required");
      return;
    }
    if (!newSupplier.gstNumber.trim()) {
      setGstNumberError("GST Number is required");
      setError("GST Number is required");
      return;
    }
    const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    if (!gstinRegex.test(newSupplier.gstNumber)) {
      setGstNumberError("Invalid GST Number format (e.g., 27AAAAA0000A1Z5)");
      setError("Invalid GST Number format");
      return;
    }
    if (newSupplier.phone.trim() && !/^[6-9]\d{9}$/.test(newSupplier.phone)) {
      setPhoneError("Please provide a valid Indian mobile number (e.g., 9876543210)");
      setError("Invalid phone number");
      return;
    }
    const fullAddress = [
      newSupplier.address,
      newSupplier.shippingAddress.flatBuildingNumber,
      newSupplier.shippingAddress.areaLocality,
      newSupplier.shippingAddress.city,
      newSupplier.shippingAddress.state,
      newSupplier.shippingAddress.pinCode
    ].filter(Boolean).join(", ");
    if (fullAddress.length > 500) {
      setError("Address cannot exceed 500 characters");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem('token');
      const payload = {
        name: newSupplier.name,
        phone: newSupplier.phone,
        email: newSupplier.email,
        address: fullAddress || newSupplier.address || "",
        gstNumber: newSupplier.gstNumber,
        currentBalance: newSupplier.currentBalance || 0,
        isActive: newSupplier.isActive,
      };

      const res = await api.post('/suppliers', payload, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });

      if (onAddSupplier) {
        onAddSupplier({
          id: res.data.data.id,
          name: res.data.data.name,
          phone: res.data.data.phone,
          email: res.data.data.email,
          address: res.data.data.address,
          gstNumber: res.data.data.gstNumber,
          currentBalance: res.data.data.currentBalance,
          isActive: res.data.data.isActive,
          createdAt: res.data.data.createdAt,
          updatedAt: res.data.data.updatedAt,
        });
      }

      // Reset form
      setNewSupplier({
        name: "",
        phone: "",
        email: "",
        address: "",
        gstNumber: "",
        currentBalance: 0,
        isActive: true,
        shippingAddress: {
          flatBuildingNumber: "",
          areaLocality: "",
          pinCode: "",
          city: "",
          state: "",
        },
      });
      setShowPartyDetails(false);
      setGstNumberError("");
      setPhoneError("");
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to add supplier');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true"></span>
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg className="h-6 w-6 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">Add New Supplier</h3>
                {error && <div className="mt-2 text-red-600 text-sm">{error}</div>}
                <div className="mt-4">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Supplier Name</label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={newSupplier.name}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    placeholder="Enter Supplier Name"
                    required
                  />
                </div>
                <div className="mt-4">
                  <label htmlFor="gstNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300">GST Number</label>
                  <input
                    type="text"
                    name="gstNumber"
                    id="gstNumber"
                    value={newSupplier.gstNumber}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    placeholder="Enter GST Number (e.g., 27AAAAA0000A1Z5)"
                    required
                  />
                  {gstNumberError && <div className="mt-1 text-red-600 text-sm">{gstNumberError}</div>}
                </div>
                <div className="mt-4">
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Phone Number<span className="text-gray-500 dark:text-gray-400"> (optional)</span></label>
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600">+91</span>
                    <input
                      type="text"
                      name="phone"
                      id="phone"
                      value={newSupplier.phone}
                      onChange={handleInputChange}
                      className="flex-1 block w-full rounded-none rounded-r-md sm:text-sm border-gray-300 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                      placeholder="Enter Phone Number"
                    />
                  </div>
                  {phoneError && <div className="mt-1 text-red-600 text-sm">{phoneError}</div>}
                </div>
                <div className="mt-4">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email<span className="text-gray-500 dark:text-gray-400"> (optional)</span></label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={newSupplier.email}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    placeholder="Enter Email"
                  />
                </div>
                <div className="mt-4">
                  <label htmlFor="currentBalance" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Initial Balance<span className="text-gray-500 dark:text-gray-400"> (optional)</span></label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">₹</span>
                    </div>
                    <input
                      type="number"
                      name="currentBalance"
                      id="currentBalance"
                      value={newSupplier.currentBalance}
                      onChange={handleInputChange}
                      className="block w-full pl-7 sm:text-sm border-gray-300 rounded-md dark:bg-gray-700 dark:text-white dark:border-gray-600"
                      placeholder="Enter initial balance (positive or negative)"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex items-center">
                    <input
                      id="isActive"
                      name="isActive"
                      type="checkbox"
                      checked={newSupplier.isActive}
                      onChange={handleInputChange}
                      className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                    />
                    <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">Active</label>
                  </div>
                </div>
                <div className="mt-6">
                  <button
                    type="button"
                    className="flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
                    onClick={() => setShowPartyDetails(!showPartyDetails)}
                  >
                    Add Address (Optional)
                    {showPartyDetails ? (<ArrowUp className="ml-2 h-4 w-4" />) : (<ArrowDown className="ml-2 h-4 w-4" />)}
                  </button>
                  {showPartyDetails && (
                    <div className="mt-4 space-y-4">
                      <h3 className="text-md font-semibold text-gray-900 dark:text-white">Address</h3>
                      <div>
                        <label htmlFor="address" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Address</label>
                        <input
                          type="text"
                          name="address"
                          id="address"
                          value={newSupplier.address}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
                          placeholder="Enter Address"
                        />
                      </div>
                      <div>
                        <label htmlFor="shippingAddress.flatBuildingNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Flat / Building Number</label>
                        <input
                          type="text"
                          name="shippingAddress.flatBuildingNumber"
                          id="shippingAddress.flatBuildingNumber"
                          value={newSupplier.shippingAddress.flatBuildingNumber}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
                        />
                      </div>
                      <div>
                        <label htmlFor="shippingAddress.areaLocality" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Area / Locality</label>
                        <input
                          type="text"
                          name="shippingAddress.areaLocality"
                          id="shippingAddress.areaLocality"
                          value={newSupplier.shippingAddress.areaLocality}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
                        />
                      </div>
                      <div>
                        <label htmlFor="shippingAddress.pinCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300">PIN Code</label>
                        <input
                          type="text"
                          name="shippingAddress.pinCode"
                          id="shippingAddress.pinCode"
                          value={newSupplier.shippingAddress.pinCode}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="shippingAddress.city" className="block text-sm font-medium text-gray-700 dark:text-gray-300">City</label>
                          <input
                            type="text"
                            name="shippingAddress.city"
                            id="shippingAddress.city"
                            value={newSupplier.shippingAddress.city}
                            onChange={handleInputChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
                          />
                        </div>
                        <div>
                          <label htmlFor="shippingAddress.state" className="block text-sm font-medium text-gray-700 dark:text-gray-300">State</label>
                          <input
                            type="text"
                            name="shippingAddress.state"
                            id="shippingAddress.state"
                            value={newSupplier.shippingAddress.state}
                            onChange={handleInputChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-2 sm:text-sm"
                    onClick={handleAddSupplier}
                    disabled={loading}
                  >
                    {loading ? 'Adding...' : 'Add Supplier'}
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                    onClick={onClose}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}